/**
 * Helper pure function — Story 11.5 frontend preview live.
 *
 * Renderiza `contentPreview` (string com placeholders `{{N}}`) substituindo
 * cada slot pelo sample value do `VARIABLE_SAMPLES` correspondente ao
 * `variable_mappings`. Mirror do backend `renderMappingPreview` (campaign-templates.ts)
 * mas client-side pra zero-latency typing UX.
 *
 * Backend endpoint POST /preview existe pra outros consumers (testing tool,
 * audit). Aqui priorizamos UX responsiva.
 */

import { VARIABLE_SAMPLES, type VariableKey } from '@/constants/variable-registry';

export interface PreviewResult {
  rendered: string;
  /** Placeholders no texto sem mapping configurado (mantidos literais). */
  missingSlots: string[];
  /** Mappings que apontam pra placeholder inexistente OU field sem sample. */
  warnings: string[];
}

const PLACEHOLDER_RE = /\{\{(\d+)\}\}/g;

export function renderTemplatePreview(
  contentPreview: string | null | undefined,
  mappings: Record<string, string>,
): PreviewResult {
  if (!contentPreview) {
    return { rendered: '', missingSlots: [], warnings: [] };
  }

  const placeholdersInText = new Set<string>();
  for (const m of contentPreview.matchAll(PLACEHOLDER_RE)) {
    placeholdersInText.add(m[1]!);
  }

  const warnings: string[] = [];
  const missingSlots: string[] = [];

  const rendered = contentPreview.replace(PLACEHOLDER_RE, (_, pos: string) => {
    const fieldKey = mappings[pos];
    if (!fieldKey) {
      missingSlots.push(`{{${pos}}}`);
      return `{{${pos}}}`;
    }
    const sample = VARIABLE_SAMPLES[fieldKey as VariableKey];
    if (sample === undefined) {
      warnings.push(
        `field_key "${fieldKey}" em {{${pos}}} não tem sample — vai resolver '' em runtime`,
      );
      return '';
    }
    return sample;
  });

  for (const pos of Object.keys(mappings)) {
    if (!placeholdersInText.has(pos)) {
      warnings.push(`Mapping {{${pos}}} não tem placeholder correspondente no template`);
    }
  }

  return { rendered, missingSlots, warnings };
}
